import { flagWords } from "../../ui/kit/FlagMark.tsx";
import { flagOf } from "./store.ts";

/**
 * The diagnostic strip (P1.9-T13; ADR-061, amendment "the design system lands": "the diagnostic strip becomes
 * dev-only behind `?diag` and takes no pointer events"): the page's connection and measurements in one line,
 * for development, read only when `?diag` asks for it. It measures things that change every tick, so it is
 * read each animation frame, and the strip is drawn only when its line differs from the one shown.
 */

/**
 * What the strip reads from the page's report.
 */
export interface DiagnosticReport {
  readonly error: string | null;
  readonly closed: string | null;
  readonly ready: boolean;
  readonly pvp: number;
  readonly tick: number;
  readonly renderLead: number;
  readonly localX: number;
  readonly localY: number;
  readonly remote: number;
  readonly corrections: readonly number[];
  readonly snapshotsDiscarded: number;
  readonly inputsSkipped: number;
  readonly debug: boolean;
}

/**
 * The strip's line, as React reads it.
 */
export interface DiagnosticsSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => string;
}

/**
 * The strip's line: the connection's state, the character's name, the flag's words, the tick and the render clock's
 * lead over it in ticks (above 2, others are drawn past the newest snapshot; ADR-032's amendment of 2026-10-03), the predicted
 * position in sixteenths, the others in view, the corrections by band, the snapshots discarded and inputs
 * skipped, the artificial latency and the keys.
 *
 * @param report The page's report.
 * @param name The character's name as the vitals frame shows it.
 * @param latencyMs The page's artificial latency.
 * @returns The line.
 * @example
 * diagnosticLine(gameReport, "Ada", 0);
 * // "connected | Ada | Protected | tick 1200 lead 0.4 | me 2048,2048 | ..."
 */
export function diagnosticLine(report: DiagnosticReport, name: string, latencyMs: number): string {
  const state = report.error !== null ? `error: ${report.error}` : report.closed !== null ? `closed: ${report.closed}` : report.ready ? "connected" : "connecting";
  const flag = flagOf(report.pvp);
  return `${state} | ${name} | ${flagWords(flag.state, flag.locked)} | tick ${report.tick} lead ${report.renderLead.toFixed(1)} | me ${report.localX},${report.localY} | ${report.remote} others | corrections soft ${report.corrections[1] ?? 0} firm ${report.corrections[2] ?? 0} snap ${report.corrections[3] ?? 0} | discarded ${report.snapshotsDiscarded} skipped ${report.inputsSkipped} | latency ${latencyMs} ms | arrows or WASD, E read, F8 collision${report.debug ? " on" : ""}`;
}

/**
 * The strip's line, telling its listeners only when it changes.
 */
export class DiagnosticsFeed implements DiagnosticsSource {
  private line = "";
  private readonly listeners = new Set<() => void>();

  /**
   * Adds a listener told of every new line. Works unbound, as React calls it.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The line shown. Works unbound, as React calls it.
   *
   * @returns The line.
   */
  public readonly getSnapshot = (): string => this.line;

  /**
   * Sets the line; the same line again tells no one.
   *
   * @param line The line.
   */
  public set(line: string): void {
    if (line === this.line) {
      return;
    }
    this.line = line;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * Reads the strip's line on every animation frame into its feed.
 *
 * @param feed The feed.
 * @param read Reads the line from the page.
 * @param frame Schedules the next read; the window's `requestAnimationFrame` by default.
 * @returns A function that stops reading.
 */
export function watchDiagnostics(feed: DiagnosticsFeed, read: () => string, frame: (callback: () => void) => number = (callback) => window.requestAnimationFrame(callback)): () => void {
  let stopped = false;

  /**
   * Reads one frame's line and asks for the next frame.
   */
  const next = (): void => {
    if (stopped) {
      return;
    }
    feed.set(read());
    frame(next);
  };
  frame(next);
  return () => {
    stopped = true;
  };
}
